/**
 * Page titles live in the top bar.
 *
 * `AppShell` provides `PageTitleSlotContext` (the bar's title element + the
 * title store). A page names itself with `<PageHeader>` or `usePageTitle`; the
 * winning registration (see `titleStore.ts`) portals its `<h1>` into the bar,
 * so the node keeps its own props, state and providers. Outside a shell the
 * context is undefined and callers render their heading in place, as before.
 */
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useSyncExternalStore,
  type JSX,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useMatches, useParentMatches } from "@tanstack/react-router";
import { BREADCRUMB_NAMESPACES } from "../Breadcrumbs/breadcrumbNamespaces";
import { nextTitleSeq, type TitleStore } from "./titleStore";

export interface PageTitleSlot {
  store: TitleStore;
  /** The bar's title element; null until the top bar has mounted. */
  target: HTMLElement | null;
}

export const PageTitleSlotContext = createContext<PageTitleSlot | undefined>(undefined);

/** The bar's `<h1>` (plus an optional control beside it, never inside it). */
export function PageTitleHeading({
  id,
  title,
  titleText,
  titleAdornment,
}: {
  id: string;
  title: ReactNode;
  titleText: string | undefined;
  titleAdornment?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex min-w-0 items-center gap-xs">
      <h1
        id={id}
        className="text-page-title truncate max-lg:text-heading"
        dir="auto"
        title={titleText}
      >
        {title}
      </h1>
      {titleAdornment !== undefined && titleAdornment !== null ? (
        <div className="flex shrink-0 items-center">{titleAdornment}</div>
      ) : null}
    </div>
  );
}

function plainText(title: ReactNode, titleText: string | undefined): string | undefined {
  if (titleText !== undefined) return titleText;
  if (typeof title === "string" || typeof title === "number") return String(title);
  return undefined;
}

function BarTitle({
  slot,
  title,
  titleText,
  titleAdornment,
}: {
  slot: PageTitleSlot;
  title: ReactNode;
  titleText: string | undefined;
  titleAdornment: ReactNode;
}): JSX.Element | null {
  const { store, target } = slot;
  const id = useId();
  const depth = useParentMatches({ select: (m) => m.length });
  const text = plainText(title, titleText);
  const textRef = useRef(text);
  textRef.current = text;

  // Re-register (new seq) only when the route depth changes; text changes
  // update in place so they never reorder the winner.
  useEffect(() => {
    store.set({ id, depth, seq: nextTitleSeq(), titleText: textRef.current });
    return () => store.remove(id);
  }, [store, id, depth]);
  useEffect(() => {
    store.setText(id, text);
  }, [store, id, text]);

  const winnerId = useSyncExternalStore(
    store.subscribe,
    () => store.getWinner()?.id ?? null,
    () => null,
  );
  if (winnerId !== id || target === null) return null;
  return createPortal(
    <PageTitleHeading id={id} title={title} titleText={text} titleAdornment={titleAdornment} />,
    target,
  );
}

export interface UsePageTitleOptions {
  title: ReactNode;
  /** Plain text for the tooltip + `document.title` when `title` is a node. */
  titleText?: string | undefined;
  /** Default true. False keeps the heading in the page (it no longer names the page). */
  inBar?: boolean;
  /** Rendered beside the bar's h1 (e.g. prev/next buttons). */
  titleAdornment?: ReactNode;
}

export interface UsePageTitleResult {
  /** False outside an AppShell — render your heading in place. */
  inShell: boolean;
  /** Render this anywhere in the page: it portals the title into the bar. */
  titleNode: ReactNode;
}

export function usePageTitle({
  title,
  titleText,
  inBar = true,
  titleAdornment,
}: UsePageTitleOptions): UsePageTitleResult {
  const slot = useContext(PageTitleSlotContext);
  const hasTitle = title !== null && title !== undefined && title !== false && title !== "";
  return {
    inShell: slot !== undefined,
    titleNode:
      slot !== undefined && inBar && hasTitle ? (
        <BarTitle slot={slot} title={title} titleText={titleText} titleAdornment={titleAdornment} />
      ) : null,
  };
}

/**
 * The deepest route's `staticData.breadcrumb` label — the bar's title when no
 * page registered one. Null when no route names itself.
 */
export function useRouteTitle(): string | null {
  const matches = useMatches();
  const { t } = useTranslation(BREADCRUMB_NAMESPACES);
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const match = matches[i]!;
    const bc = match.staticData?.breadcrumb;
    if (!bc) continue;
    let label: string | null = null;
    if (bc.resolve) {
      const loaderData = match.loaderData as { breadcrumbLabel?: string } | undefined;
      const params = match.params as Record<string, string> | undefined;
      label = bc.resolve({
        ...(loaderData !== undefined ? { loaderData } : {}),
        ...(params !== undefined ? { params } : {}),
      });
    } else if (bc.labelKey) {
      label = t(bc.labelKey, { defaultValue: bc.labelKey });
    }
    if (label !== null && label !== "") return label;
  }
  return null;
}
